// Canvas core. Legacy: initCanvas/resizeCanvas/zoom/grid (snapshot L3193-3292,
// 3878-3946, 5410-5477). Fabric v7 changes applied (map §4):
//   - named import `Canvas` (not `new fabric.Canvas`)
//   - prefer `requestRenderAll()`
//   - the canvas singleton lives in state.ts, not a file-scope global
import { Canvas, FabricText, Line, type FabricObject } from 'fabric'
import { state } from './state'
import { CANVAS_PADDING, MM_TO_PX } from './constants'

type Flagged = FabricObject & { isGrid?: boolean; isBgImage?: boolean }

const CANVAS_EL_ID = 'labelCanvas'

// Fabric v7 changed the default text origin to center/center (v5 was left/top).
// The entire legacy editor positions text by its top-left corner, so restore the
// v5 default globally for all text classes (IText/Textbox inherit FabricText).
// Intentional center cases (e.g. the warning "!") set originX/Y explicitly.
FabricText.ownDefaults.originX = 'left'
FabricText.ownDefaults.originY = 'top'

/** Millimetre dimensions of the current label, honouring orientation. */
export function labelMm(): { w: number; h: number } {
  const { widthMM, heightMM } = state.currentSize
  return state.orientation === 'portrait'
    ? { w: heightMM, h: widthMM }
    : { w: widthMM, h: heightMM }
}

/** Pixel dimensions of the current label, honouring orientation. */
export function labelPx(): { w: number; h: number } {
  const { w, h } = labelMm()
  return { w: w * MM_TO_PX, h: h * MM_TO_PX }
}

/** Create the single Fabric v7 canvas and store it in state. Returns it. */
export function initCanvas(): Canvas {
  const el = document.getElementById(CANVAS_EL_ID) as HTMLCanvasElement | null
  if (!el) throw new Error(`#${CANVAS_EL_ID} canvas element not found`)

  const { w, h } = labelPx()
  const canvas = new Canvas(el, {
    width: w,
    height: h,
    backgroundColor: '#ffffff',
    preserveObjectStacking: true,
    selection: true,
  })

  state.canvas = canvas
  resizeCanvas()

  // Scroll-wheel zoom (centered). Wheel up = zoom in, down = zoom out; prevent the
  // page from scrolling. Matches the live editor's wheel-to-zoom behaviour.
  canvas.on('mouse:wheel', (opt) => {
    const e = opt.e as WheelEvent
    e.preventDefault()
    e.stopPropagation()
    const next = state.zoomFactor * (e.deltaY > 0 ? 0.92 : 1.08)
    setZoom(next)
  })

  // Snap-to-grid (legacy snapObjectToGrid on object:moving): snap the object's
  // bounding-box edges — not its center-origin left/top — to grid multiples.
  canvas.on('object:moving', (opt) => {
    if (!state.snapToGrid || !state.gridEnabled) return
    const obj = opt.target as Flagged | undefined
    if (!obj || obj.isGrid || obj.isBgImage) return
    const step = state.gridSize * MM_TO_PX
    if (step < 2) return
    obj.setCoords() // mid-drag the cached coords lag behind left/top
    const b = obj.getBoundingRect()
    obj.set({
      left: (obj.left ?? 0) + (Math.round(b.left / step) * step - b.left),
      top: (obj.top ?? 0) + (Math.round(b.top / step) * step - b.top),
    })
  })

  // Phase 1+: wire cross-domain handlers here (selection:created/updated/cleared,
  // object:modified -> saveState, text:changed -> autoFitText, etc.) by importing
  // handlers from their modules — see map §3.3 item 1.
  return canvas
}

/**
 * Redraw the grid overlay from state (legacy drawGrid/clearGrid). Lines are
 * scene-space (zoom-independent), flagged isGrid so serialization, layers,
 * stats and exports all exclude them. Loading a design wipes them (they're
 * plain canvas objects), so every load path calls this again.
 */
export function refreshGrid(): void {
  const canvas = state.canvas
  if (!canvas) return
  canvas.getObjects().filter((o) => (o as Flagged).isGrid).forEach((o) => canvas.remove(o))
  if (state.gridEnabled) {
    const { w, h } = labelPx()
    const step = state.gridSize * MM_TO_PX
    if (step >= 2) {
      const props = {
        stroke: '#cbd5e1', strokeWidth: 0.5, selectable: false, evented: false,
        excludeFromExport: true, opacity: 0.4, originX: 'left' as const, originY: 'top' as const,
      }
      for (let x = step; x < w; x += step) {
        const l = new Line([x, 0, x, h], props) as Flagged
        l.isGrid = true
        canvas.add(l)
      }
      for (let y = step; y < h; y += step) {
        const l = new Line([0, y, w, y], props) as Flagged
        l.isGrid = true
        canvas.add(l)
      }
    }
  }
  canvas.requestRenderAll()
}

/** Resize the backing canvas to the current label size (legacy resizeCanvas). */
export function resizeCanvas(): void {
  const canvas = state.canvas
  if (!canvas) return
  const { w, h } = labelPx()
  canvas.setDimensions({ width: w, height: h })
  applyCanvasShape()
  refreshGrid()
  canvas.requestRenderAll()
}

/** Visually clip the editing canvas to the label's die-cut shape (round/oval),
 *  with a dashed guide outline. Rect → no clip. (Legacy updateCircleOverlay.)
 *
 *  The clip goes on the LOWER canvas only (the label + objects layer). Fabric
 *  draws selection handles/cursor on a separate UPPER canvas, so clipping the
 *  wrapper would cut handles of objects at the edge — clipping just the lower
 *  canvas keeps the round label while leaving controls fully visible. */
export function applyCanvasShape(): void {
  const canvas = state.canvas
  if (!canvas) return
  const c = canvas as unknown as { wrapperEl?: HTMLElement; lowerCanvasEl?: HTMLElement }
  const wrapper = c.wrapperEl
  const lower = c.lowerCanvasEl
  if (!wrapper) return
  const shape = state.currentSize.shape
  const round = shape === 'round' || shape === 'oval'
  // Rounded-rect toggle (legacy: 3.175mm / 0.125" die-cut radius). The radius is
  // an ELEMENT-px style, so scale by zoom; setZoom re-invokes this function.
  const rounded = !round && state.roundedCorners
  const radius = round ? '50%' : rounded ? `${(3.175 * MM_TO_PX * state.zoomFactor).toFixed(1)}px` : ''
  if (lower) lower.style.borderRadius = radius // clip the label bitmap, not the controls
  // wrapper itself is NOT clipped (keep overflow visible so edge handles show)
  wrapper.style.borderRadius = ''
  wrapper.style.overflow = ''
  let guide = wrapper.querySelector<HTMLElement>('.olm-shape-guide')
  if (!round && !rounded) { guide?.remove(); return }
  if (!guide) {
    guide = document.createElement('div')
    guide.className = 'olm-shape-guide'
    wrapper.appendChild(guide)
  }
  // Match the lower canvas box exactly (it can be offset within the wrapper).
  guide.style.cssText = `position:absolute;left:${lower?.offsetLeft ?? 0}px;top:${lower?.offsetTop ?? 0}px;` +
    `width:${lower?.offsetWidth ?? wrapper.offsetWidth}px;height:${lower?.offsetHeight ?? wrapper.offsetHeight}px;` +
    `border-radius:${radius};border:2px dashed rgba(96,165,250,0.45);pointer-events:none;z-index:10;box-sizing:border-box;`
}

export function setZoom(factor: number): void {
  const canvas = state.canvas
  if (!canvas) return
  factor = Math.max(0.1, Math.min(8, factor))
  state.zoomFactor = factor
  canvas.setZoom(factor)
  const { w, h } = labelPx()
  canvas.setDimensions({ width: w * factor, height: h * factor })
  applyCanvasShape() // rounded-corner radius is zoom-dependent
  canvas.requestRenderAll()
  // Notify the toolbar (and anyone else) so the zoom % display stays in sync.
  document.dispatchEvent(new CustomEvent('olm:zoom', { detail: factor }))
}

export function zoomIn(): void { setZoom(state.zoomFactor + 0.1) }
export function zoomOut(): void { setZoom(state.zoomFactor - 0.1) }

/** Fit the label to the visible container (legacy fitToView). */
export function fitToView(): void {
  const canvas = state.canvas
  const container = document.getElementById('canvasContainer')
  if (!canvas || !container) return
  const { w, h } = labelPx()
  const availW = container.clientWidth - CANVAS_PADDING * 2
  const availH = container.clientHeight - CANVAS_PADDING * 2
  const factor = Math.max(0.1, Math.min(availW / w, availH / h, 8))
  setZoom(factor)
}
